import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, FlexControlledForm, Inscription, LinkButton, useDebounceState } from '@common-fe/common-fe';
import _ from 'lodash';

import { ExternalLink } from '@/common/constants';
import regexp from '@/common/regexp';
import usePhoneVerificationFields, { FormValues } from '@/modules/user/components/Verification/hooks/usePhoneVerificationFields';
import useSendVerificationCodeQuery from '@/modules/user/components/Verification/queries/useSendVerificationCode.query';
import {
  PhoneVerificationStep,
  VerificationErrorText,
  VerificationServerError,
  VerificationType,
} from '@/modules/user/components/Verification/verification.types';
import useUpdateEmployeeQuery from '@/modules/user/queries/useUpdateEmployee.query';
import { useValidateForm } from '@/utils/hooks';
import { whiteLabelingConfig } from '@/utils/whiteLabeling';

import VerificationModalWithWarning from '../components/VerificationModalWithWarning';

interface Props {
  phoneNumber: string;
  employerPhoneNumber?: string;
  setPhoneNumber: (value: string) => void;
  onSetVisible: (value: boolean) => void;
  setStep: (step: PhoneVerificationStep) => void;
}

const EnterPhoneNumberVerificationModal: React.FC<Props> = ({ phoneNumber, employerPhoneNumber, setPhoneNumber, onSetVisible, setStep }) => {
  const { t } = useTranslation();
  const [errorText, setErrorText] = useState<string | undefined>(undefined);
  const [state, handleSetState] = useState<FormValues>();
  const { values, setValues } = useDebounceState<FormValues>();
  const fields = usePhoneVerificationFields({ phoneNumber, errorText });
  const { handlePreSubmit, hasErrors } = useValidateForm(fields);
  const { sendVerificationCode, isLoading } = useSendVerificationCodeQuery();
  const { updateEmployee } = useUpdateEmployeeQuery();

  const handleClick = useCallback((link: string) => {
    if (link) {
      window.open(link, '_blank');
    }
  }, []);

  const handleCancel = useCallback(() => {
    onSetVisible(false);
  }, [onSetVisible]);

  const handleEnableSmsNotifications = useCallback(async () => {
    try {
      await updateEmployee({ is_sms_notification_enabled: true });
      setStep(PhoneVerificationStep.PHONE_VERIFIED_SUCCESSFULLY);
    } catch (error) {
      setErrorText(VerificationErrorText.OTHER_ERROR);
    }
  }, [updateEmployee, setStep]);

  const handleNext = useCallback(async () => {
    setErrorText(undefined);
    try {
      const isSuccess = handlePreSubmit(state);
      if (isSuccess) { 
        await sendVerificationCode({
          type: VerificationType.SMS,
          value: state?.phoneNumber || ''
        });
        setPhoneNumber(state?.phoneNumber || '');
        setStep(PhoneVerificationStep.PHONE_VERIFICATION_CODE);
      }
    } catch (error) {
      const errorMessage = _.get(error, 'response.data.elevate_error_message', '') as string;

      if (errorMessage.includes(VerificationServerError.PHONE_NUMBER_ALREADY_VERIFIED)) {
        handleEnableSmsNotifications();
        return;
      }

      if (errorMessage.includes(VerificationServerError.CODE_RESEND_TIMEOUT_ERROR)) {
        setErrorText(VerificationErrorText.CODE_RESEND_TIMEOUT_ERROR);
        return;
      }

      setErrorText(VerificationErrorText.OTHER_ERROR);
    }
  }, [sendVerificationCode, setPhoneNumber, setStep, handlePreSubmit, handleEnableSmsNotifications, state]);

  const isEmployerPhoneUsed = useMemo(() => {
    const employerPhoneNumbers = employerPhoneNumber?.replace(regexp.ALL_NON_NUMERIC, '');
    const currentPhoneNumbers = values?.phoneNumber?.replace(regexp.ALL_NON_NUMERIC, '');
    return employerPhoneNumbers && employerPhoneNumbers === currentPhoneNumbers;
  }, [values, employerPhoneNumber]);

  useEffect(() => {
    if (values) {
      handleSetState(values);
    }
  }, [handleSetState, values]);

  useEffect(() => {
    if (errorText === VerificationErrorText.PHONE_NUMBER_IS_ALREADY_VERIFIED) {
      setErrorText(undefined);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values?.phoneNumber]);

  return (
    <VerificationModalWithWarning
      title={t('Stay in the Know')}
      subTitle={t('Setup text alerts and never miss an update.')}
      isLoading={isLoading}
      onSetVisible={onSetVisible}
      onCancel={handleCancel}
      onNext={handleNext}
      warningText={(
        <Inscription>
          {t('By opting in to text messaging, you  agree to the ')}
          <LinkButton onClick={() => handleClick(whiteLabelingConfig?.termsAndConditions || ExternalLink.termsAndCondition)} linkInlineMode>
            {t('Terms of Service')}
          </LinkButton>
          {t(' and ')}
          <LinkButton onClick={() => handleClick( whiteLabelingConfig?.privacyPolicy || ExternalLink.privacyPolicy)} linkInlineMode>
            {t('Privacy Policy')}
          </LinkButton>
          {t('. Msg & Data rates may apply. You can turn off text messaging any time.')}
        </Inscription>
      )}
      form={(
        <FlexControlledForm
          fields={fields}
          formStyle={{ margin: '24px' }}
          onChangeValues={setValues}
          showError={hasErrors}
          editMode
        >
          {isEmployerPhoneUsed && (
            <Box
              margin={{ top: '-12px' }}
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(200px, 1fr) 320px' ,
              }}
            >
              <Box />
              <Inscription size="small" color="textSecondary">
                {t('Employer provided phone number')}
              </Inscription>
            </Box>
          )}
        </FlexControlledForm>
      )}
    />
  );
};

export default EnterPhoneNumberVerificationModal;
